import { useEffect, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import type { Route } from 'app/navigation/interfaces'
import { homePath } from 'app/navigation/paths'

import { getIsAuthorized, routesPermissions } from 'common/auth/permissions'
import Button from 'common/components/button'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import {
  Account,
  changeAccountId,
  getSelectedAccountName,
} from 'features/auth/store/authSlice'

import AccountSelectButtonContent from './components/AccountSelectButtonContent'
import SearchInput from './components/SearchInput'

import styles from './AccountSelect.module.css'

interface Props {
  accounts: Account[]
  accountsRoute: Route
}

const filterAccountsBySearch = (accounts: Account[], searchValue: string) =>
  accounts.filter(({ name }) =>
    name.toLowerCase().includes(searchValue.toLowerCase())
  )

const AccountSelector = ({ accounts, accountsRoute }: Props) => {
  const dispatch = useAppDispatch()
  const history = useHistory()

  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const containerRef = useRef<HTMLDivElement>(null)

  const close = () => setIsClosing(true)
  const handleAnimationEnd = () => {
    if (isClosing) {
      setIsClosing(false)
      setIsDropdownVisible(false)
    }
  }

  const { accountId, permissions } = useAppSelector((state) => state.authStates)
  const accountName = useAppSelector(getSelectedAccountName)

  useEffect(() => {
    if (!isDropdownVisible) {
      return
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close()
      }
    }
    document.addEventListener('mousedown', handleClickOutside, true)
    return () =>
      document.removeEventListener('mousedown', handleClickOutside, true)
  }, [isDropdownVisible])

  const onSelectAccount = async (id: string): Promise<void> => {
    close()
    await dispatch(changeAccountId(id)).then(() => {
      history.replace(homePath)
    })
  }

  const hasAccountsViewPermissions = getIsAuthorized(
    permissions,
    routesPermissions.accountsView
  )

  const filteredAccounts = searchValue
    ? filterAccountsBySearch(accounts, searchValue)
    : accounts

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <Button
        data-cy={`${accountName}-button`}
        data-testid="account-selection-button"
        className="min-w-0 text-white pr-0"
        onClick={() => {
          if (isDropdownVisible) {
            close()
          } else {
            setIsDropdownVisible(true)
          }
        }}
      >
        <AccountSelectButtonContent
          accountName={accountName}
          isDropdownVisible={isDropdownVisible}
        />
      </Button>

      {(isDropdownVisible || isClosing) && (
        <div
          data-dropdown-menu
          className={`box-border border border-solid border-coolGray-300 text-base-700 bg-white absolute right-0 mt-1 pt-2 ${styles.container}`}
          data-testid="account-selector-menu"
          style={{
            zIndex: 1070,
            minWidth: '240px',
            width: 'max-content',
            borderRadius: '3px',
            animation: isClosing
              ? 'dropdown-slide-out 0.3s cubic-bezier(0.6, 0.04, 0.98, 0.34) forwards'
              : 'dropdown-slide-in 0.3s cubic-bezier(0.08, 0.82, 0.17, 1)',
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onAnimationEnd={handleAnimationEnd}
        >
          <div className="pt-2 px-4 pb-4">
            <SearchInput
              data-testid="account-search"
              value={searchValue}
              onChange={(value) => setSearchValue(value)}
            />
          </div>
          <ul role="menu" className="max-h-90 overflow-auto list-none m-0 p-0">
            {filteredAccounts.map(({ id, name }) => (
              <li
                role="menuitem"
                key={id}
                data-cy={`${name}-account-menu-item`}
                data-testid="account-menu-item"
                onClick={() => onSelectAccount(id)}
                className="relative bg-white pt-4 px-4 pb-2 hover:bg-coolGray-50 hover:text-maroon-400 cursor-pointer"
              >
                {name}
                {id === accountId && (
                  <span
                    aria-hidden="true"
                    className="absolute text-maroon-400 font-bold"
                    style={{
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    ✓
                  </span>
                )}
              </li>
            ))}
          </ul>
          {hasAccountsViewPermissions && (
            <div
              data-testid="edit-accounts-menu-item"
              className="pt-4 px-4 pb-4 border-t border-coolGray-200 cursor-pointer"
            >
              <Link to={accountsRoute.to}>Edit Accounts</Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AccountSelector
